/**
 * The live store: the socket, the HTTP routes and the cache behind one
 * interface.
 *
 * Everything a screen can see comes through `RelayStore`, and everything under
 * it is here. `socket.ts` knows how to be connected, `frames.ts` is the wire,
 * `payload.ts` is what is inside an entry, `api.ts` is the HTTP surface; this
 * file is the only thing that holds state, and the only thing that decides what
 * any of it means.
 *
 * ── Three rules that are not style ──────────────────────────────────────────
 *
 * **Every getter returns a stable value.** `useStore` is
 * `useSyncExternalStore`, which compares with `Object.is`: a getter that builds
 * a fresh array on every call re-renders for ever. Anything that filters, maps
 * or sorts goes through `memo`, which is cleared once per change.
 *
 * **A write applies locally and renders now.** The ack clears `pending`, the
 * nack marks it failed, and a retry reuses the message id — it is the server's
 * dedup key, so a fresh id per attempt turns every dropped socket into a
 * duplicated message.
 *
 * **Nothing here escapes anything.** A body, a quote's author and excerpt and
 * an attachment's name are a stranger's text and stay text; they are escaped
 * where they are drawn, by React, and a second escaping here would put a
 * literal `&amp;` on screen.
 *
 * ── What is in memory and what is on disk ───────────────────────────────────
 * Messages live in memory for the life of the page. Only the cursors are
 * persisted, under the account they belong to: a cursor does not go back, so
 * adopting another account's would cost that account its own half of every
 * conversation, permanently. The desktop learned that one the hard way — its
 * `applyEntry` carries the story.
 */

import { useCallback, useSyncExternalStore } from 'react';
import * as http from './api';
import { LIMITS } from './frames';
import type {
  AckFrame, AlertFrame, BatchFrame, CallStateFrame, DeliverFrame, EphFrame, GapFrame,
  LogEntry, NackFrame, NewsFrame, ReadPosFrame, ServerFrame, SyncFrame, WatchEntry
} from './frames';
import {
  amendmentOf, decodeEntry, decodeEphemeral, decodePayload, encodeCallEnd, encodeDelete,
  encodeEdit, encodeEphemeral, encodeMsg, encodeReaction, mentionsOf, payloadName,
  presenceFromWire, presenceToWire
} from './payload';
import type { Amendment, PresencePayload, TypingPayload } from './payload';
import { sampleMode } from './sample-mode';
import { SampleRelayStore } from './sample-store';
import { REAL_TIMERS, RelaySocket, deviceId } from './socket';
import type { SocketState, Timers, WebSocketLike } from './socket';
import { laneOf, previewOf, threadOf } from './thread';
import type {
  CallFrame, CallFrameEvent, CallOutcome, CallState, Conversation, ConversationKind, IncomingCall,
  Meeting, MeetingInvitee, MeetingParticipant, MeetingPolicy, MeetingRole, MeetingStatus, Message,
  Person, Presence, Quote, Reaction, RelayStore, ThreadView, Unsubscribe
} from './types';
import { attachmentOf, type SendWithFiles } from './cloud-picker';

export { SampleRelayStore } from './sample-store';

// ── the clock ───────────────────────────────────────────────────────────────

/** A typing frame is re-sent while somebody keeps typing, and expires on the
 *  receiver shortly after the sender would have refreshed it. Six against four
 *  is the desktop's margin: one dropped frame does not blink the dots out. */
const TYPING_REFRESH_MS = 4000;
const TYPING_TTL_MS = 6000;

/** Presence is broadcast per conversation on a slow loop and decays on the
 *  receiver at two and a half times it — so a client that died or lost its
 *  network reads as offline, which is honest, rather than staying online for
 *  ever. */
const PRESENCE_BROADCAST_MS = 60000;
const PRESENCE_TTL_MS = 150000;

/** Housekeeping: expire typing nobody refreshed, decay presence nobody
 *  re-broadcast. One timer for both. */
const SWEEP_MS = 1500;

const DEFAULT_POLICY: MeetingPolicy = {
  mic: 'everyone',
  camera: 'everyone',
  screen: 'everyone',
  lobby: 'strangers'
};

/** The same empty array every time, for the reason `memo` exists. */
const NOBODY: MeetingParticipant[] = [];
const NO_ONE: Person[] = [];
const NONE_HELD: ReadonlySet<string> = new Set<string>();

const CURSOR_KEY = 'relay.cursors';

let counter = 0;

/** Client-minted, and the server's dedup key. Random rather than a counter
 *  alone: two tabs mint ids from the same code at the same millisecond. */
function freshId(prefix: string): string {
  counter += 1;
  const random = Math.floor(Math.random() * 0xffffff).toString(36);
  return `${prefix}${Date.now().toString(36)}${counter.toString(36)}${random}`;
}

// ── what the store holds per conversation ───────────────────────────────────

interface Row {
  id: string;
  /** The server's kind. `self` is derived and is not on the wire. */
  kind: 'direct' | 'group' | 'channel';
  title: string;
  topic: string;
  members: string[];
  /** False until `GET /api/relay/conversations/:id` has answered. The directory
   *  row carries no members, and a group of one is only a self-note when the
   *  membership is actually known — deriving it from an empty list would draw
   *  every conversation as a scratchpad for as long as the detail is in
   *  flight. */
  membersKnown: boolean;
  ownerId: string;
  createdAt: number;
  /** The server's flag, never inferred from a title or a sender's name. */
  official: boolean;
}

export interface LiveStoreOptions {
  /**
   * What the socket is made of.
   *
   * The store builds its own `RelaySocket` either way — the socket's callbacks
   * are this object's methods, and handing one in already wired would be a
   * circle. What a test wants is a fake `WebSocket` and a fake clock, and those
   * are what this carries.
   */
  socket?: {
    open?: (url: string) => WebSocketLike;
    url?: string;
    deviceId?: string;
    timers?: Timers;
    random?: () => number;
  };
  timers?: Timers;
  now?: () => number;
  /** False in a test: `boot()` is then the caller's to drive. */
  autoStart?: boolean;
}

export class LiveRelayStore implements RelayStore {
  private listeners = new Set<() => void>();
  private incoming = new Set<(call: IncomingCall) => void>();
  private callFrames = new Set<(event: CallFrameEvent) => void>();
  private derived = new Map<string, unknown>();

  private readonly timers: Timers;
  private readonly now: () => number;
  private readonly socket: RelaySocket;
  private readonly autoStart: boolean;

  private selfPerson: Person | null = null;
  private peopleById = new Map<string, Person>();
  private askedFor = new Set<string>();

  private rows = new Map<string, Row>();
  private log = new Map<string, Message[]>();
  /** Every message id a conversation has held, deleted ones included: a
   *  deleted id stays so a later pull cannot bring the message back, and it
   *  is what keeps the replies to a deleted root out of the transcript. */
  private held = new Map<string, Set<string>>();
  /** An `edit` or `del` whose target has not arrived, by conversation and
   *  target, in log order. A reload pulls the newest page first, so these are
   *  ordinary, and dropping them would bring a deleted message back the moment
   *  somebody scrolled up to it. */
  private amendments = new Map<
    string,
    Map<string, { seq: number; senderId: string; amendment: Amendment }[]>
  >();
  private heads = new Map<string, number>();
  private epochs = new Map<string, number>();
  private floors = new Map<string, number>();
  private cursors = new Map<string, number>();
  private reads = new Map<string, Map<string, number>>();
  private calls = new Map<string, CallState>();

  private typingBy = new Map<string, Map<string, number>>();
  private typingSentAt = new Map<string, number>();
  private presenceBy = new Map<string, { presence: Presence; at: number }>();
  private myPresence: Presence = 'online';

  private meetingList: Meeting[] = [];
  private lobbies = new Map<string, MeetingParticipant[]>();
  private askedLobbies = new Set<string>();
  private joinTokens = new Map<string, string>();

  /** A post waiting for its `ack`, by message id. Replayed on every `sync`,
   *  under the same id — which is what makes a reconnect cost nothing. */
  private outbox = new Map<string, { conversationId: string; payload: string }>();
  private olderWaiters = new Map<string, (() => void)[]>();

  private booted = false;
  private cursorsDirty = false;
  private sweeper: number | null = null;
  private presenceLoop: number | null = null;
  private socketState: SocketState = 'idle';

  constructor(options: LiveStoreOptions = {}) {
    this.timers = options.timers ?? REAL_TIMERS;
    this.now = options.now ?? Date.now;
    this.autoStart = options.autoStart ?? true;
    this.socket = new RelaySocket({
      deviceId: options.socket?.deviceId ?? deviceId(),
      onHello: () => this.watchList(),
      onFrame: (frame) => this.handle(frame),
      onState: (state) => {
        this.socketState = state;
        this.changed();
      },
      ...(options.socket?.open ? { open: options.socket.open } : {}),
      ...(options.socket?.url ? { url: options.socket.url } : {}),
      ...(options.socket?.timers ? { timers: options.socket.timers } : {}),
      ...(options.socket?.random ? { random: options.socket.random } : {})
    });
  }

  /** The socket's state, for a chrome strip that wants to say "Reconnecting".
   *  Not on `RelayStore`: nothing above the store may branch on it. */
  connection(): SocketState {
    return this.socketState;
  }

  // ── lifecycle ─────────────────────────────────────────────────────────────

  subscribe(listener: () => void): Unsubscribe {
    this.listeners.add(listener);
    // The first screen to look is what starts the client. An explicit
    // `start()` in main.tsx would be one more thing to forget, and forgetting
    // it has no symptom but an app that is simply empty.
    if (this.autoStart) void this.boot();
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Sign in to the socket. Idempotent, and safe to call from a render path.
   *
   * The directory first, so `hello` carries the conversations this account is
   * in; the socket second; the meetings last, because nothing on the first
   * screen waits on them. A failed fetch does not stop the socket — being
   * unable to read the directory is exactly when the socket's `sync` is the
   * thing that repairs it.
   *
   * **No account, nothing to boot.** `/m/:token` is outside the sign-in gate
   * and imports this module, so a guest's first `useStore` starts all of the
   * above on a browser with no session: the directory and the meetings answer
   * 401 and the socket is refused its upgrade and retries on a backoff, for
   * ever. Everywhere else the gate has already called `adopt`, so `loadMe`
   * returns early and this is never reached.
   */
  async boot(): Promise<void> {
    if (this.booted) return;
    this.booted = true;

    await this.loadMe();
    if (!this.selfPerson) return;
    this.restoreCursors();
    await this.loadConversations();
    this.socket.start();
    this.startTimers();
    void this.loadMeetings();
  }

  /** Drop the socket and the timers. The state stays: this is a page being put
   *  away, not an account being signed out of. */
  stop(): void {
    this.socket.stop();
    if (this.sweeper !== null) this.timers.clearInterval(this.sweeper);
    if (this.presenceLoop !== null) this.timers.clearInterval(this.presenceLoop);
    this.sweeper = null;
    this.presenceLoop = null;
  }

  /**
   * Adopt the account the sign-in gate already fetched.
   *
   * The gate asks `GET /api/relay/me` to decide whether to show the app at all,
   * so the answer is already in hand by the time anything here runs. This is
   * the seam the scaffold left unjoined.
   */
  adopt(person: Person): void {
    this.selfPerson = person;
    this.peopleById.set(person.id, person);
    this.changed();
  }

  private startTimers(): void {
    if (this.sweeper === null) {
      this.sweeper = this.timers.setInterval(() => this.sweep(), SWEEP_MS);
    }
    if (this.presenceLoop === null) {
      this.presenceLoop = this.timers.setInterval(
        () => this.broadcastPresence(),
        PRESENCE_BROADCAST_MS
      );
    }
  }

  private memo<T>(key: string, build: () => T): T {
    if (!this.derived.has(key)) this.derived.set(key, build());
    return this.derived.get(key) as T;
  }

  private changed(): void {
    this.derived.clear();
    if (this.cursorsDirty) this.persistCursors();
    for (const listener of this.listeners) listener();
  }

  // ── reads ─────────────────────────────────────────────────────────────────

  me(): Person | null {
    return this.selfPerson;
  }

  conversations(): Conversation[] {
    return this.memo('conversations', () => {
      const out: Conversation[] = [];
      for (const row of this.rows.values()) out.push(this.viewOf(row));
      return out.sort((a, b) => b.lastAt - a.lastAt);
    });
  }

  /** The transcript lane. `thread.ts` has the rules. */
  messages(conversationId: string): Message[] {
    return this.memo(`messages:${conversationId}`, () =>
      laneOf(this.log.get(conversationId) ?? [], this.held.get(conversationId) ?? NONE_HELD)
    );
  }

  thread(conversationId: string, rootMessageId: string): ThreadView {
    return this.memo(`thread:${conversationId}:${rootMessageId}`, () =>
      threadOf(
        this.log.get(conversationId) ?? [],
        this.held.get(conversationId) ?? NONE_HELD,
        rootMessageId
      )
    );
  }

  newestSeq(conversationId: string): number {
    return this.cursors.get(conversationId) ?? 0;
  }

  people(ids: string[]): Person[] {
    if (ids.length === 0) return NO_ONE;
    return this.memo(`people:${ids.join(',')}`, () => {
      const out: Person[] = [];
      for (const id of ids) {
        const person = this.peopleById.get(id);
        if (person) out.push(person);
        else this.askAbout(id);
      }
      return out;
    });
  }

  presenceOf(userId: string): Presence {
    if (this.selfPerson && userId === this.selfPerson.id) return this.myPresence;
    return this.presenceBy.get(userId)?.presence ?? 'offline';
  }

  typing(conversationId: string): Person[] {
    const entries = this.typingBy.get(conversationId);
    if (!entries || entries.size === 0) return NO_ONE;
    const mine = this.selfPerson?.id;
    return this.people([...entries.keys()].filter((id) => id !== mine));
  }

  callState(conversationId: string): CallState | null {
    return this.calls.get(conversationId) ?? null;
  }

  meetings(): Meeting[] {
    return this.meetingList;
  }

  hasOlder(conversationId: string): boolean {
    const { lowest, floor } = this.olderRange(conversationId);
    return lowest > floor;
  }

  lobby(meetingId: string): MeetingParticipant[] {
    const held = this.lobbies.get(meetingId);
    if (held) return held;
    this.askAboutLobby(meetingId);
    return NOBODY;
  }

  // ── building a conversation ───────────────────────────────────────────────

  private viewOf(row: Row): Conversation {
    const messages = this.log.get(row.id) ?? [];
    // A reply moves the row; only the transcript writes its line.
    const last = messages[messages.length - 1];
    const shown = previewOf(messages);
    const kind = this.kindOf(row);
    return {
      id: row.id,
      kind,
      title: this.titleOf(row, kind),
      topic: row.topic,
      members: row.members,
      ownerId: row.ownerId,
      unread: this.unreadOf(row.id),
      ...(this.mentionedOf(row.id) ? { mentioned: true } : {}),
      lastAt: last ? last.at : row.createdAt,
      // A call record and a meeting card have no words of their own; their row
      // is drawn from what happened. An empty preview is the honest line.
      preview: shown ? shown.body : '',
      ...(row.official ? { official: true } : {})
    };
  }

  /**
   * `self` is the client's distinction, not the server's.
   *
   * relay-server stores the self-note as a group of one member, because a
   * fourth kind on the wire would be a fourth kind in every membership check.
   * A group of one is drawn as a scratchpad rather than as a group, and that
   * is the whole of the difference.
   */
  private kindOf(row: Row): ConversationKind {
    if (row.kind === 'direct') return 'direct';
    // Mauvely and the account: a channel, however few people the server lists.
    if (row.official) return 'channel';
    if (row.membersKnown && row.members.length <= 1) return 'self';
    return row.kind;
  }

  private titleOf(row: Row, kind: ConversationKind): string {
    if (kind !== 'direct') return row.title;
    const mine = this.selfPerson?.id;
    const other = row.members.find((id) => id !== mine);
    if (!other) return row.title;
    const person = this.peopleById.get(other);
    if (person) return person.name;
    this.askAbout(other);
    // The id rather than an empty row: a nameless conversation reads as a bug,
    // and this is visible for one round trip.
    return row.title || other;
  }

  private unreadOf(conversationId: string): number {
    const head = this.heads.get(conversationId) ?? 0;
    const mine = this.selfPerson?.id;
    const read = mine ? this.reads.get(conversationId)?.get(mine) ?? 0 : 0;
    return Math.max(0, head - read);
  }

  /** The desktop's `mentioned`: a message somebody else wrote, past your read
   *  position, names you. Derived rather than stored, so reading clears it. */
  private mentionedOf(conversationId: string): boolean {
    const mine = this.selfPerson?.id;
    if (!mine) return false;
    const read = this.reads.get(conversationId)?.get(mine) ?? 0;
    const messages = this.log.get(conversationId) ?? [];
    for (let i = messages.length - 1; i >= 0 && messages[i]!.seq > read; i--) {
      const message = messages[i]!;
      if (message.senderId !== mine && message.mentions?.includes(mine)) return true;
    }
    return false;
  }

  private askAbout(userId: string): void {
    if (!userId || this.askedFor.has(userId) || this.peopleById.has(userId)) return;
    this.askedFor.add(userId);
    void http
      .getUser(userId)
      .then(({ user }) => {
        this.peopleById.set(user.id, personOf(user, this.presenceOf(user.id)));
        this.changed();
      })
      .catch(() => {
        // A deactivated or deleted account answers 404. Asking again on every
        // render would be a request per frame for somebody who is not coming
        // back, so the id stays in `askedFor` and the row draws from what the
        // payload said.
      });
  }

  private rememberName(userId: string, name: string): void {
    if (!userId || !name || this.peopleById.has(userId)) return;
    this.peopleById.set(userId, {
      id: userId,
      name,
      presence: this.presenceOf(userId)
    });
  }

  // ── loading ───────────────────────────────────────────────────────────────

  private async loadMe(): Promise<void> {
    if (this.selfPerson) return;
    try {
      const { user } = await http.getMe();
      this.selfPerson = personOf(user, this.myPresence);
      this.peopleById.set(user.id, this.selfPerson);
      this.changed();
    } catch {
      // 401 is the sign-in gate's business, and the gate has already run. Any
      // other failure is the socket's to report.
    }
  }

  private async loadConversations(): Promise<void> {
    try {
      const { conversations } = await http.listConversations();
      const listed = new Set(conversations.map((row) => row.id));
      // A membership alert after a deletion or a removal means this listing is
      // the first place the loss is visible: anything held that is no longer
      // named here is gone, not merely unmentioned. A failed listing (the
      // catch below) must drop nothing — it says nothing about what changed.
      this.forgetConversations([...this.rows.keys()].filter((id) => !listed.has(id)));
      const fresh = conversations.filter((row) => !this.rows.has(row.id));
      for (const row of conversations) {
        const existing = this.rows.get(row.id);
        this.rows.set(row.id, {
          id: row.id,
          kind: kindFromWire(row.kind),
          title: row.title,
          topic: row.topic,
          members: existing?.members ?? [],
          membersKnown: existing?.membersKnown ?? false,
          ownerId: existing?.ownerId ?? row.createdBy,
          createdAt: row.createdAt,
          official: row.official === true
        });
      }
      this.changed();
      // A listing is not a subscription. Somebody added us while this page was
      // open, and the row appearing is all the alert has done: without a watch
      // its messages arrive nowhere until the next reconnect. Before the socket
      // opens this sends nothing, and `hello` carries every row.
      if (fresh.length > 0) {
        this.socket.send({
          t: 'watch',
          add: fresh.map((row) => ({ c: row.id, seq: this.cursors.get(row.id) ?? 0 }))
        });
      }
      await this.loadMembers(conversations.map((row) => row.id));
    } catch {
      // Offline, or the Worker is between deploys. `sync` repairs the list.
    }
  }

  /**
   * Drop conversations from local state and stop watching them.
   *
   * The one cleanup `leave()` and a pruned listing both need: the row, the
   * log and the held-id set, plus telling the socket so a later `hello` does
   * not ask for something that is gone. One `watch` frame for every id here,
   * not one per id — the same shape `hello`'s own watch list is sent in.
   */
  private forgetConversations(ids: string[]): void {
    if (ids.length === 0) return;
    for (const id of ids) {
      this.rows.delete(id);
      this.log.delete(id);
      this.held.delete(id);
      this.amendments.delete(id);
    }
    this.socket.send({ t: 'watch', remove: ids });
  }

  /**
   * Members, four conversations at a time.
   *
   * The directory row has none: they live in the Durable Object and the list
   * route deliberately does not wake one per conversation. So this is one call
   * each, and the concurrency cap is there because a person in forty channels
   * would otherwise open forty subrequests in one tick.
   */
  private async loadMembers(ids: string[], limit = 4): Promise<void> {
    const queue = [...ids];
    const workers = Array.from({ length: Math.min(limit, queue.length) }, async () => {
      for (;;) {
        const id = queue.shift();
        if (!id) return;
        try {
          const detail = await http.getConversation(id);
          this.adoptDetail(detail);
        } catch {
          // Removed between the list and this call, most likely. The row stays
          // with no members and reads as a group, which is what it was.
        }
      }
    });
    await Promise.all(workers);
    this.changed();
  }

  private adoptDetail(detail: http.WireConversation): void {
    const existing = this.rows.get(detail.id);
    this.rows.set(detail.id, {
      id: detail.id,
      kind: kindFromWire(detail.kind),
      title: detail.title,
      topic: detail.topic,
      members: detail.members,
      membersKnown: true,
      ownerId: detail.owner ?? existing?.ownerId ?? '',
      createdAt: existing?.createdAt ?? this.now(),
      official: detail.official === true
    });
    this.heads.set(detail.id, Math.max(this.heads.get(detail.id) ?? 0, detail.head));
    this.epochs.set(detail.id, detail.epoch);
    this.floors.set(detail.id, detail.availableFrom);
    for (const member of detail.members) this.askAbout(member);
  }

  private async loadMeetings(): Promise<void> {
    try {
      const { meetings } = await http.listMeetings();
      this.meetingList = meetings
        .filter((m) => !m.deletedAt)
        .map((m) => this.meetingOf(m))
        .sort((a, b) => (b.scheduledAt || 0) - (a.scheduledAt || 0));
      this.changed();
    } catch {
      // Meetings are a page, not the app. A failure here leaves it empty.
    }
  }

  private askAboutLobby(meetingId: string): void {
    if (this.askedLobbies.has(meetingId)) return;
    this.askedLobbies.add(meetingId);
    void this.refreshLobby(meetingId);
  }

  private async refreshLobby(meetingId: string): Promise<void> {
    try {
      const { waiting } = await http.getLobby(meetingId);
      this.lobbies.set(
        meetingId,
        waiting.map((party) => ({
          id: party.id,
          displayName: party.displayName,
          guest: party.kind === 'guest',
          role: 'attendee' as MeetingRole,
          muted: false,
          admission: 'knocking' as const
        }))
      );
      this.changed();
    } catch {
      // Not the host. An empty lobby is the right answer for everybody else,
      // and the route says so with a 403 rather than a list.
      this.lobbies.set(meetingId, NOBODY);
      this.changed();
    }
  }

  // ── the socket ────────────────────────────────────────────────────────────

  /**
   * `hello`'s watch list: every conversation and the highest seq held.
   *
   * Asked at every open, so a resume carries what the last connection learnt.
   * Capped at the server's own bound — an add past 512 is refused and shows up
   * as absence from the answering `sync`, which is a conversation that silently
   * stops updating.
   */
  private watchList(): WatchEntry[] {
    const ids = this.rows.size > 0 ? [...this.rows.keys()] : [...this.cursors.keys()];
    return ids.slice(0, LIMITS.maxWatch).map((c) => ({ c, seq: this.cursors.get(c) ?? 0 }));
  }

  private handle(frame: ServerFrame): void {
    switch (frame.t) {
      case 'sync':
        this.onSync(frame as SyncFrame);
        break;
      case 'deliver': {
        const deliver = frame as DeliverFrame;
        this.applyEntries(deliver.c, deliver.entries ?? []);
        break;
      }
      case 'news': {
        const news = frame as NewsFrame;
        this.heads.set(news.c, Math.max(this.heads.get(news.c) ?? 0, news.head));
        // Above the fan-out threshold the server coalesces and tells us to
        // fetch. A client that only handled `deliver` would work in every test
        // and go silent in exactly the channels where silence is loudest.
        this.pull(news.c, (this.cursors.get(news.c) ?? 0) + 1);
        break;
      }
      case 'batch':
        this.onBatch(frame as BatchFrame);
        break;
      case 'gap':
        this.onGap(frame as GapFrame);
        break;
      case 'ack':
        this.onAck(frame as AckFrame);
        break;
      case 'nack':
        this.onNack(frame as NackFrame);
        break;
      case 'alert':
        this.onAlert(frame as AlertFrame);
        break;
      case 'eph':
        this.onEphemeral(frame as EphFrame);
        break;
      case 'readpos': {
        const readpos = frame as ReadPosFrame;
        this.setRead(readpos.c, readpos.user, readpos.seq);
        break;
      }
      case 'callstate':
        this.onCallState(frame as CallStateFrame);
        break;
      default:
        // A frame from a newer server. Ignored, which is the posture every
        // shipped client takes — the alert codes are additive and the version
        // does not move for them.
        return;
    }
    this.changed();
  }

  private onSync(frame: SyncFrame): void {
    for (const entry of frame.conversations ?? []) {
      this.heads.set(entry.c, Math.max(this.heads.get(entry.c) ?? 0, entry.head));
      this.epochs.set(entry.c, entry.epoch);
      this.floors.set(entry.c, entry.availableFrom);
      // The server's reading of our cursor, after clamping. Adopting it is what
      // stops a pull from below `joined_seq` repeating for ever.
      this.setCursor(entry.c, entry.cursor);
      if (entry.reads) {
        for (const [user, seq] of Object.entries(entry.reads)) this.setRead(entry.c, user, seq);
      }
      this.setCall(entry.c, entry.call ?? null);
      if (!this.rows.has(entry.c)) {
        // A conversation the directory did not list: somebody added us while
        // this page was open. `sync` is the first anyone hears of it.
        void this.loadOne(entry.c);
      }
      // ── A page that has just loaded holds nothing ───────────────────────
      // The log lives in memory and the cursor lives in `localStorage`, so a
      // reload arrives claiming to hold everything and holding none of it: the
      // watch list says "I am at seq 47", the server has nothing above 47 to
      // send, and the transcript stays empty for ever. Reported as "message
      // history doesn't load and doesn't persist", and it is the second half
      // that explains the first.
      //
      // So when there is no log for a conversation, ask for the *tail* rather
      // than from the cursor — and rather than from seq 1, which would answer a
      // long channel with its oldest sixty-four messages and none of today's.
      const haveLog = (this.log.get(entry.c)?.length ?? 0) > 0;
      if (!haveLog && entry.head > 0) {
        this.pull(entry.c, Math.max(entry.availableFrom, entry.head - LIMITS.defaultPull + 1));
      } else if (entry.head > entry.cursor) {
        this.pull(entry.c, entry.cursor + 1);
      }
    }
    // Anything that never got its ack goes again, under the same id. The server
    // answers a repeat with the seq the original earned and `duplicate: true`,
    // which is exactly what a reconnect needs.
    for (const [id, held] of this.outbox) this.post(id, held.conversationId, held.payload);
    this.broadcastPresence();
  }

  private async loadOne(conversationId: string): Promise<void> {
    try {
      const detail = await http.getConversation(conversationId);
      this.adoptDetail(detail);
      this.changed();
    } catch {
      // Refused or gone. Nothing to draw.
    }
  }

  private onBatch(frame: BatchFrame): void {
    const entries = frame.entries ?? [];
    this.heads.set(frame.c, Math.max(this.heads.get(frame.c) ?? 0, frame.head));
    this.applyEntries(frame.c, entries);
    const last = entries[entries.length - 1];
    if (frame.more && last) {
      // Truncated at `limit`. Pull again from the last seq + 1.
      this.pull(frame.c, last.seq + 1);
      return;
    }
    this.resolveOlder(frame.c);
  }

  /**
   * History was archived out from under us.
   *
   * The one case that warrants telling somebody messages are missing — and the
   * reason it must not be collapsed with a short answer from below
   * `joined_seq`, which is not a hole and would put "messages may be missing"
   * at the top of every conversation anyone has ever joined.
   */
  private onGap(frame: GapFrame): void {
    this.floors.set(frame.c, frame.availableFrom);
    this.setCursor(frame.c, Math.max(this.cursors.get(frame.c) ?? 0, frame.availableFrom - 1));
    this.resolveOlder(frame.c);
    this.pull(frame.c, frame.availableFrom);
  }

  private onAck(frame: AckFrame): void {
    this.outbox.delete(frame.id);
    this.patchMessage(frame.c, frame.id, (message) => ({
      ...message,
      seq: frame.seq,
      pending: undefined,
      // Set from every ack, including one that carries nobody, so a retry that
      // now succeeds clears a mark the first attempt left.
      undeliveredTo: frame.unreachable?.length ? frame.unreachable : undefined
    }));
    this.sortLog(frame.c);
    this.advance(frame.c, frame.seq);
  }

  private onNack(frame: NackFrame): void {
    this.outbox.delete(frame.id);
    // Failed, and it says so. A message that sits "Sending" for ever is the
    // worse answer: the person cannot tell whether to type it again.
    this.patchMessage(frame.c, frame.id, (message) => ({ ...message, pending: 'failed' }));
  }

  private onAlert(frame: AlertFrame): void {
    if (frame.code === 'membership') {
      void this.loadConversations();
      return;
    }
    if (frame.code === 'meeting') {
      // A nudge, never the record. The invitation is a row and survives a
      // closed app; losing this frame costs a refresh.
      void this.loadMeetings();
    }
  }

  private onCallState(frame: CallStateFrame): void {
    this.setCall(frame.c, { since: frame.since, callId: frame.callId, roster: frame.roster ?? [] });
  }

  private setCall(
    conversationId: string,
    call: { since: number; callId: string; roster: CallState['roster'] } | null
  ): void {
    // An empty roster is sent, never implied: it is the frame that takes a
    // conversation's call bar down.
    if (!call || call.roster.length === 0) {
      this.calls.delete(conversationId);
      return;
    }
    this.calls.set(conversationId, {
      conversationId,
      callId: call.callId,
      since: call.since,
      roster: call.roster
    });
  }

  // ── the log ───────────────────────────────────────────────────────────────

  private applyEntries(conversationId: string, entries: LogEntry[]): void {
    for (const entry of entries) {
      const ids = this.ids(conversationId);
      // Our own envelopes come back around. The test is whether we hold the
      // message, not whether we sent it: a device that kept its id and lost its
      // memory is an ordinary reload, and answering "ours, skip it" for one
      // would advance the cursor past our own half of the conversation.
      if (ids.has(entry.id)) {
        // One thing is still worth reading off an envelope we already hold: a
        // mark we have not got. The ack normally brings it first, but an ack is
        // one frame to one device and a dropped socket loses it — after which
        // the entry is the only thing that will ever say so.
        if (entry.unreachable?.length) {
          this.patchMessage(conversationId, entry.id, (message) => ({
            ...message,
            undeliveredTo: entry.unreachable
          }));
        }
        this.advance(conversationId, entry.seq);
        continue;
      }
      this.advance(conversationId, entry.seq);

      const payload = decodePayload(entry.payload);
      if (!payload) continue;
      const name = payloadName(payload);
      if (name) this.rememberName(entry.sender, name);

      // `rct`, `edit` and `del` change a message this client already holds
      // rather than adding one, so none of them reaches `decodeEntry`.
      if (payload.t === 'rct') {
        this.applyReaction(conversationId, entry.sender, payload as Record<string, unknown>);
        continue;
      }
      const amendment = amendmentOf(payload);
      if (amendment) {
        this.amend(conversationId, entry.seq, entry.sender, amendment);
        continue;
      }

      const message = decodeEntry(conversationId, entry, payload);
      if (!message) continue;

      // The message is the typing ending. Waiting for the expiry would draw
      // somebody typing under the thing they typed.
      this.stopTyping(conversationId, entry.sender);
      this.append(conversationId, message);
    }
  }

  /**
   * A reaction, folded onto the message it names.
   *
   * An unknown target is ordinary and not an error: a reaction to something
   * below our `joined_seq`, or to a message its author has since deleted.
   * There is nothing to attach it to and nothing to say about it.
   */
  private applyReaction(
    conversationId: string,
    senderId: string,
    wire: Record<string, unknown>
  ): void {
    const messageId = typeof wire['mid'] === 'string' ? wire['mid'] : '';
    const emoji = typeof wire['e'] === 'string' ? wire['e'] : '';
    if (!messageId || !emoji) return;
    this.patchMessage(conversationId, messageId, (message) =>
      foldReaction(message, emoji, senderId, wire['on'] === true)
    );
  }

  /** Toggle an emoji on a message. Applies locally and posts — a reaction that
   *  waited for the round trip would feel like a dropped click. */
  react(conversationId: string, messageId: string, emoji: string, on: boolean): void {
    const mine = this.selfPerson?.id;
    if (!mine || !emoji) return;
    this.patchMessage(conversationId, messageId, (message) =>
      foldReaction(message, emoji, mine, on)
    );
    const payload = encodeReaction(messageId, emoji, on);
    const id = freshId('r_');
    this.outbox.set(id, { conversationId, payload });
    this.post(id, conversationId, payload);
    this.changed();
  }

  private ids(conversationId: string): Set<string> {
    let set = this.held.get(conversationId);
    if (!set) {
      set = new Set<string>();
      this.held.set(conversationId, set);
    }
    return set;
  }

  private append(conversationId: string, arrived: Message): void {
    const list = this.log.get(conversationId) ?? [];
    this.ids(conversationId).add(arrived.id);
    // Anything that named this message before it arrived, in the order it was
    // written, and to the same author rule as an amendment that finds its
    // target at once.
    let message = arrived;
    const waiting = this.amendments.get(conversationId);
    for (const held of waiting?.get(arrived.id) ?? []) {
      if (held.senderId !== arrived.senderId) continue;
      if (held.amendment.kind === 'del') {
        waiting?.delete(arrived.id);
        return;
      }
      message = { ...message, body: held.amendment.body };
    }
    waiting?.delete(arrived.id);
    list.push(message);
    list.sort((a, b) => a.seq - b.seq);
    this.log.set(conversationId, list);
  }

  /**
   * An `edit` or a `del` from the log.
   *
   * **The author rule is enforced here, on receipt**, against the server's
   * stamp: a rule kept only at the sending keyboard is one a modified client
   * ignores. The target is looked up in this conversation only, so a `mid`
   * naming another conversation's message changes nothing.
   *
   * A target this conversation has never held is waited for (`amendments`);
   * one it held and deleted is gone, and an edit to it is nothing. The
   * desktop drops an early one too, but it keeps its log on disk; this one
   * starts every page load from the newest page.
   */
  private amend(
    conversationId: string,
    seq: number,
    senderId: string,
    amendment: Amendment
  ): void {
    const target = this.log.get(conversationId)?.find((m) => m.id === amendment.messageId);
    if (!target) {
      if (this.ids(conversationId).has(amendment.messageId)) return;
      let waiting = this.amendments.get(conversationId);
      if (!waiting) {
        waiting = new Map();
        this.amendments.set(conversationId, waiting);
      }
      // In seq order, not arrival order: a page from further back can bring an
      // older edit after a newer one, and the newer must still win.
      const list = waiting.get(amendment.messageId) ?? [];
      list.push({ seq, senderId, amendment });
      list.sort((a, b) => a.seq - b.seq);
      waiting.set(amendment.messageId, list);
      return;
    }
    if (target.senderId !== senderId) return;
    if (amendment.kind === 'edit') {
      this.patchMessage(conversationId, target.id, (message) => ({
        ...message,
        body: amendment.body
      }));
    } else {
      this.removeMessage(conversationId, target.id);
    }
  }

  /** Out of the log, and only out of the log: the id stays in `held`. */
  private removeMessage(conversationId: string, messageId: string): void {
    const list = this.log.get(conversationId);
    const index = list?.findIndex((m) => m.id === messageId) ?? -1;
    if (list && index >= 0) list.splice(index, 1);
  }

  private sortLog(conversationId: string): void {
    const list = this.log.get(conversationId);
    if (list) list.sort((a, b) => a.seq - b.seq);
  }

  private patchMessage(
    conversationId: string,
    messageId: string,
    patch: (message: Message) => Message
  ): void {
    const list = this.log.get(conversationId);
    if (!list) return;
    const index = list.findIndex((m) => m.id === messageId);
    if (index < 0) return;
    const current = list[index];
    if (!current) return;
    list[index] = patch(current);
  }

  private advance(conversationId: string, seq: number): void {
    if (seq <= 0) return;
    this.heads.set(conversationId, Math.max(this.heads.get(conversationId) ?? 0, seq));
    this.setCursor(conversationId, Math.max(this.cursors.get(conversationId) ?? 0, seq));
  }

  private setCursor(conversationId: string, seq: number): void {
    const held = this.cursors.get(conversationId) ?? 0;
    // A cursor does not go back.
    const next = Math.max(held, seq);
    if (next === held && this.cursors.has(conversationId)) return;
    this.cursors.set(conversationId, next);
    this.cursorsDirty = true;
  }

  private setRead(conversationId: string, userId: string, seq: number): void {
    let byUser = this.reads.get(conversationId);
    if (!byUser) {
      byUser = new Map<string, number>();
      this.reads.set(conversationId, byUser);
    }
    // Monotonic, like the server's own.
    byUser.set(userId, Math.max(byUser.get(userId) ?? 0, seq));
  }

  private pull(conversationId: string, from: number, limit?: number): void {
    if (from < 1) return;
    this.socket.send({
      t: 'pull',
      c: conversationId,
      from,
      ...(limit ? { limit: Math.min(limit, LIMITS.maxPull) } : {})
    });
  }

  private resolveOlder(conversationId: string): void {
    const waiters = this.olderWaiters.get(conversationId);
    if (!waiters) return;
    this.olderWaiters.delete(conversationId);
    for (const resolve of waiters) resolve();
  }

  // ── writes ────────────────────────────────────────────────────────────────

  /**
   * The lowest seq held, and the floor there is nothing below.
   *
   * `loadOlder` and `hasOlder` ask the same question and one of them acts on
   * the answer, so the expression lives here rather than twice — a transcript
   * that watched for a boolean the loader disagreed with would ask for a page
   * for ever, or stop one short of the floor.
   */
  private olderRange(conversationId: string): { lowest: number; floor: number } {
    const list = this.log.get(conversationId) ?? [];
    const first = list.find((m) => m.seq > 0);
    return {
      lowest: first ? first.seq : (this.cursors.get(conversationId) ?? 0) + 1,
      floor: Math.max(1, this.floors.get(conversationId) ?? 1)
    };
  }

  /**
   * Earlier history.
   *
   * Resolves when the range has landed, or immediately when the conversation is
   * already at `availableFrom` — there is nothing below it, and a promise that
   * never settled would leave a spinner at the top of the transcript for ever.
   */
  loadOlder(conversationId: string): Promise<void> {
    const { lowest, floor } = this.olderRange(conversationId);
    if (lowest <= floor) return Promise.resolve();

    const from = Math.max(floor, lowest - LIMITS.defaultPull);
    return new Promise<void>((resolve) => {
      const waiters = this.olderWaiters.get(conversationId) ?? [];
      waiters.push(resolve);
      this.olderWaiters.set(conversationId, waiters);
      this.pull(conversationId, from, lowest - from);
    });
  }

  send(conversationId: string, body: string, opts?: SendWithFiles): string {
    const self = this.selfPerson;
    const id = freshId('m_');
    const quote = opts?.quoteId ? this.quoteFor(opts.quoteId) : undefined;
    // Filtered as the wire is, so this copy holds what the others will.
    const mentions = mentionsOf(opts?.mentions);
    const files = opts?.files ?? [];
    const message: Message = {
      id,
      conversationId,
      // Provisional, and above everything held so it sorts last. The ack
      // replaces it with the real one.
      seq: (this.heads.get(conversationId) ?? 0) + this.outbox.size + 1,
      senderId: self?.id ?? '',
      at: this.now(),
      body,
      attachments: files.map((f) => attachmentOf(f)),
      kind: 'msg',
      pending: 'sending',
      ...(opts?.replyTo ? { replyTo: opts.replyTo } : {}),
      ...(quote ? { quote } : {}),
      ...(mentions.length > 0 ? { mentions } : {})
    };
    this.append(conversationId, message);

    const payload = encodeMsg({
      body,
      name: self?.name ?? '',
      ...(opts?.replyTo ? { replyTo: opts.replyTo } : {}),
      ...(quote ? { quote } : {}),
      ...(files.length > 0 ? { files } : {}),
      ...(mentions.length > 0 ? { mentions } : {})
    });
    this.outbox.set(id, { conversationId, payload });
    this.post(id, conversationId, payload);
    this.setTyping(conversationId, false);
    this.changed();
    return id;
  }

  private post(id: string, conversationId: string, payload: string): void {
    this.socket.send({
      t: 'post',
      c: conversationId,
      id,
      kind: 'application',
      epoch: this.epochs.get(conversationId) ?? 0,
      payload
    });
    // A `false` here is a socket that is down. The message stays pending and
    // the next `sync` replays the outbox under the same id, which is the whole
    // reason the id is minted by the client.
  }

  private quoteFor(messageId: string): Quote | undefined {
    for (const list of this.log.values()) {
      for (const message of list) {
        if (message.id !== messageId) continue;
        const author = this.peopleById.get(message.senderId)?.name ?? '';
        // Plain text, one line, clamped. The excerpt travels with the quote
        // because the receiver may not hold the quoted message at all.
        return {
          messageId,
          author,
          excerpt: message.body.replace(/\s+/g, ' ').trim().slice(0, 160)
        };
      }
    }
    // An unresolvable target sends as an ordinary message rather than carrying
    // a quote of nothing.
    return undefined;
  }

  setTyping(conversationId: string, on: boolean): void {
    const now = this.now();
    if (on) {
      const sent = this.typingSentAt.get(conversationId) ?? 0;
      // One frame per refresh interval while somebody keeps typing, not one per
      // keystroke.
      if (now - sent < TYPING_REFRESH_MS) return;
      this.typingSentAt.set(conversationId, now);
    } else {
      this.typingSentAt.delete(conversationId);
    }
    this.sendEphemeral(conversationId, {
      v: 1,
      t: 'typ',
      on,
      n: this.selfPerson?.name ?? ''
    } satisfies TypingPayload);
  }

  setPresence(presence: Presence): void {
    if (this.myPresence === presence) return;
    this.myPresence = presence;
    if (this.selfPerson) {
      this.selfPerson = { ...this.selfPerson, presence };
      this.peopleById.set(this.selfPerson.id, this.selfPerson);
    }
    this.broadcastPresence();
    this.changed();
  }

  private broadcastPresence(): void {
    for (const id of this.rows.keys()) {
      this.sendEphemeral(id, {
        v: 1,
        t: 'prs',
        p: presenceToWire(this.myPresence),
        n: this.selfPerson?.name ?? ''
      } satisfies PresencePayload);
    }
  }

  private sendEphemeral(conversationId: string, payload: object): boolean {
    const encoded = encodeEphemeral(payload as never);
    // Over the cap. The server treats that as fatal and closes the socket, so
    // refusing here loses a typing dot and sending would lose the session.
    if (encoded === null) return false;
    return this.socket.send({ t: 'eph', c: conversationId, payload: encoded });
  }

  async lookup(handleOrEmail: string): Promise<Person | null> {
    const raw = handleOrEmail.trim();
    if (!raw) return null;
    // Split on '@': a leading one is how somebody writes a username, and an
    // address is the only other thing that carries one. Exact match either way
    // — a prefix search over the accounts table would be an enumeration
    // service.
    const handle = raw.startsWith('@') ? raw.slice(1) : raw;
    const byEmail = !raw.startsWith('@') && raw.includes('@');
    try {
      const { user } = byEmail
        ? await http.getUserByEmail(raw.toLowerCase())
        : await http.getUserByHandle(handle);
      const person = personOf(user, this.presenceOf(user.id));
      this.peopleById.set(person.id, person);
      this.changed();
      return person;
    } catch {
      // 404 is "nobody has that", which is an answer and not an error.
      return null;
    }
  }

  async createConversation(
    kind: Exclude<ConversationKind, 'self'>,
    title: string,
    memberIds: string[]
  ): Promise<string> {
    const created = await http.createConversation({
      kind,
      ...(kind === 'direct' ? {} : { title }),
      members: memberIds
    });
    await this.loadOne(created.id);
    // Watched without a reconnect, or the first message in it arrives nowhere.
    this.socket.send({ t: 'watch', add: [{ c: created.id, seq: 0 }] });
    this.changed();
    return created.id;
  }

  async addMember(conversationId: string, userId: string): Promise<void> {
    await http.addMember(conversationId, userId);
    await this.loadOne(conversationId);
  }

  async removeMember(conversationId: string, userId: string): Promise<void> {
    await http.removeMember(conversationId, userId);
    await this.loadOne(conversationId);
  }

  async leave(conversationId: string): Promise<void> {
    const mine = this.selfPerson?.id;
    if (!mine) return;
    // Leaving is removing yourself, which is the same route.
    await http.removeMember(conversationId, mine);
    this.forgetConversations([conversationId]);
    this.changed();
  }

  /** The owner's delete-for-everyone. `leave` is your own exit; this takes the
   *  room down, and the membership alert every other member gets is what
   *  prunes their own copy through `loadConversations()`. */
  async deleteForEveryone(conversationId: string): Promise<void> {
    await http.deleteConversation(conversationId);
    this.forgetConversations([conversationId]);
    this.changed();
  }

  async rename(conversationId: string, title: string, topic?: string): Promise<void> {
    const after = await http.patchConversation(conversationId, {
      title,
      ...(topic === undefined ? {} : { topic })
    });
    const row = this.rows.get(conversationId);
    // The route answers with the two fields it changed, not a conversation —
    // no id and no members. Adopting it as a detail would write a row keyed
    // `undefined` and lose the membership this one already has.
    if (row) this.rows.set(conversationId, { ...row, title: after.title, topic: after.topic });
    this.changed();
  }

  markRead(conversationId: string, seq: number): void {
    const mine = this.selfPerson?.id;
    if (!mine || seq <= 0) return;
    const held = this.reads.get(conversationId)?.get(mine) ?? 0;
    // Monotonic — the server ignores a lower value, so sending one is a frame
    // spent to be told nothing.
    if (seq <= held) return;
    this.setRead(conversationId, mine, seq);
    this.socket.send({ t: 'read', c: conversationId, seq });
    this.changed();
  }

  /** Your own message, by its author's rule — which every receiver applies
   *  again against the server's stamp, so this copy only keeps the screen
   *  honest. */
  private ownMessage(conversationId: string, messageId: string): Message | undefined {
    const mine = this.selfPerson?.id;
    const target = this.log.get(conversationId)?.find((m) => m.id === messageId);
    return mine && target?.senderId === mine ? target : undefined;
  }

  editMessage(conversationId: string, messageId: string, body: string): void {
    const text = body.trim();
    const target = this.ownMessage(conversationId, messageId);
    if (!target || target.kind !== 'msg' || !text) return;
    this.patchMessage(conversationId, messageId, (message) => ({ ...message, body: text }));
    this.postAmendment(conversationId, freshId('e_'), encodeEdit(messageId, text));
  }

  deleteMessage(conversationId: string, messageId: string): void {
    if (!this.ownMessage(conversationId, messageId)) return;
    this.removeMessage(conversationId, messageId);
    // Deleting an unacked send retracts it: the next `sync` would otherwise
    // post the message this just removed.
    this.outbox.delete(messageId);
    this.postAmendment(conversationId, freshId('d_'), encodeDelete(messageId));
  }

  /** Applies-then-posts, like a reaction: into the outbox so a reconnect
   *  replays it under the same id. */
  private postAmendment(conversationId: string, id: string, payload: string): void {
    this.outbox.set(id, { conversationId, payload });
    this.post(id, conversationId, payload);
    this.changed();
  }

  // ── ephemerals in ─────────────────────────────────────────────────────────

  private onEphemeral(frame: EphFrame): void {
    const payload = decodeEphemeral(frame.payload);
    if (!payload) return;
    const kind = payload.t;
    const fromSelf = frame.from === this.selfPerson?.id;

    /*
     * Our own other devices: their typing is not news, and our own presence
     * already lives here. **A call is the exception**, and the test is on the
     * kind rather than on the sender: answering on the phone has to stop the
     * desktop ringing, and there is nobody but ourselves who can say so. The
     * server already relays to every device but the sending one.
     *
     * Widen this to admit every kind and you watch your own typing dots.
     */
    if (fromSelf && kind !== 'call') return;

    const name = typeof (payload as { n?: unknown }).n === 'string'
      ? (payload as { n: string }).n
      : '';
    if (!fromSelf && name) this.rememberName(frame.from, name);

    if (kind === 'call') {
      this.onCallFrame(frame, payload as CallFrame, fromSelf);
      return;
    }
    if (kind === 'typ') {
      if ((payload as TypingPayload).on === true) {
        this.typingFor(frame.c).set(frame.from, this.now() + TYPING_TTL_MS);
      } else {
        this.stopTyping(frame.c, frame.from);
      }
      return;
    }
    if (kind === 'prs') {
      const presence = presenceFromWire((payload as PresencePayload).p);
      if (!presence) return;
      this.presenceBy.set(frame.from, { presence, at: this.now() });
      const person = this.peopleById.get(frame.from);
      if (person) this.peopleById.set(frame.from, { ...person, presence });
      return;
    }
    // A kind a newer build invented. Skipped, same posture as the log.
  }

  private typingFor(conversationId: string): Map<string, number> {
    let entries = this.typingBy.get(conversationId);
    if (!entries) {
      entries = new Map<string, number>();
      this.typingBy.set(conversationId, entries);
    }
    return entries;
  }

  private stopTyping(conversationId: string, userId: string): void {
    this.typingBy.get(conversationId)?.delete(userId);
  }

  private onCallFrame(frame: EphFrame, call: CallFrame, fromSelf: boolean): void {
    const event: CallFrameEvent = {
      conversationId: frame.c,
      fromUserId: frame.from,
      fromDeviceId: frame.device,
      fromSelf,
      action: call.a,
      callId: call.id,
      ...(typeof call.cam === 'boolean' ? { camera: call.cam } : {}),
      ...(typeof call.n === 'string' ? { fromName: call.n } : {})
    };
    for (const listener of this.callFrames) listener(event);

    // The invitation, and the only frame that carries a name and a camera.
    // Never for our own: another of our devices starting a call is not a call
    // to answer.
    if (call.a !== 'ring' || fromSelf) return;
    const incoming: IncomingCall = {
      conversationId: frame.c,
      callId: call.id,
      fromUserId: frame.from,
      fromName: typeof call.n === 'string' ? call.n : '',
      camera: call.cam === true
    };
    for (const listener of this.incoming) listener(incoming);
  }

  private sweep(): void {
    const now = this.now();
    let moved = false;

    for (const [conversationId, entries] of this.typingBy) {
      for (const [userId, expires] of entries) {
        if (expires > now) continue;
        entries.delete(userId);
        moved = true;
      }
      if (entries.size === 0) this.typingBy.delete(conversationId);
    }

    for (const [userId, held] of this.presenceBy) {
      // Offline is the honest reading of a client that died or lost its
      // network; no dot at all means never heard from, which is a different
      // thing and is what an absent entry says.
      if (held.presence === 'offline' || now - held.at <= PRESENCE_TTL_MS) continue;
      this.presenceBy.set(userId, { presence: 'offline', at: held.at });
      const person = this.peopleById.get(userId);
      if (person) this.peopleById.set(userId, { ...person, presence: 'offline' });
      moved = true;
    }

    if (moved) this.changed();
  }

  // ── meetings ──────────────────────────────────────────────────────────────

  private meetingOf(wire: http.WireMeeting): Meeting {
    return {
      id: wire.id,
      ownerId: wire.ownerId,
      title: wire.title,
      scheduledAt: wire.scheduledAt ?? 0,
      status: statusOf(wire.status),
      invitees: inviteesOf(wire.invitees, wire.cohosts),
      conversationId: wire.conversationId ?? '',
      policy: policyOf(wire.policy),
      recording: wire.recording === true,
      own: wire.ownerId === this.selfPerson?.id
    };
  }

  async createMeeting(opts: {
    title: string;
    scheduledAt?: number;
    conversationId?: string;
    invitees?: MeetingInvitee[];
    policy?: Partial<MeetingPolicy>;
  }): Promise<Meeting> {
    const wire = await http.createMeeting({
      title: opts.title,
      ...(opts.scheduledAt ? { scheduledAt: opts.scheduledAt } : {}),
      ...(opts.conversationId ? { conversationId: opts.conversationId } : {}),
      ...(opts.invitees ? { invitees: opts.invitees } : {}),
      ...(opts.policy ? { policy: { ...DEFAULT_POLICY, ...opts.policy } } : {})
    });
    // The one time the join token is ever returned. Kept, so asking for the
    // link later does not have to rotate it out from under whoever holds it.
    if (wire.joinToken) this.joinTokens.set(wire.id, wire.joinToken);
    const meeting = this.meetingOf(wire);
    this.meetingList = [meeting, ...this.meetingList.filter((m) => m.id !== meeting.id)];
    this.changed();
    return meeting;
  }

  async endMeeting(meetingId: string): Promise<void> {
    await http.endMeeting(meetingId);
    this.patchMeetingStatus(meetingId, 'ended');
  }

  async cancelMeeting(meetingId: string): Promise<void> {
    await http.cancelMeeting(meetingId);
    this.patchMeetingStatus(meetingId, 'cancelled');
  }

  private patchMeetingStatus(meetingId: string, status: MeetingStatus): void {
    this.meetingList = this.meetingList.map((m) => (m.id === meetingId ? { ...m, status } : m));
    this.changed();
  }

  /**
   * The guest link.
   *
   * The token is returned once, at create. A host who did not keep it can only
   * rotate — which is not a limitation to work around but the revocation
   * mechanism: the link is the whole credential, so the only way to learn one
   * is to make the old one stop working.
   */
  async joinLink(meetingId: string, opts?: { rotate?: boolean }): Promise<string> {
    const held = this.joinTokens.get(meetingId);
    if (held && !opts?.rotate) return this.linkFor(held);
    const { joinToken } = await http.rotateLink(meetingId);
    this.joinTokens.set(meetingId, joinToken);
    return this.linkFor(joinToken);
  }

  private linkFor(token: string): string {
    const origin = typeof window === 'undefined' ? 'https://relay.mauvely.com' : window.location.origin;
    return `${origin}/m/${token}`;
  }

  async admit(meetingId: string, partyId: string): Promise<void> {
    await http.admitParty(meetingId, partyId);
    await this.refreshLobby(meetingId);
  }

  async deny(meetingId: string, partyId: string): Promise<void> {
    await http.denyParty(meetingId, partyId);
    await this.refreshLobby(meetingId);
  }

  /**
   * A guest redeeming a link. Additive to `RelayStore`: only `/m/:token` calls
   * it, and only a party with no account reaches that route.
   */
  async joinByToken(token: string, displayName?: string): Promise<http.WireJoinAnswer> {
    return http.joinMeeting(token, displayName);
  }

  // ── ringing ───────────────────────────────────────────────────────────────

  onIncomingCall(cb: (call: IncomingCall) => void): Unsubscribe {
    this.incoming.add(cb);
    return () => {
      this.incoming.delete(cb);
    };
  }

  /** Every call frame, including our own other devices'. A `decline` from the
   *  phone is how the desktop stops ringing, and nothing but this says so. */
  onCallFrames(cb: (event: CallFrameEvent) => void): Unsubscribe {
    this.callFrames.add(cb);
    return () => {
      this.callFrames.delete(cb);
    };
  }

  ring(conversationId: string, callId: string, opts?: { camera?: boolean }): void {
    // Only the invitation carries these: a `join` repeating the caller's name
    // would be a second copy of a fact the ring already established.
    this.sendCall(conversationId, {
      v: 1,
      t: 'call',
      a: 'ring',
      id: callId,
      cam: opts?.camera === true,
      n: this.selfPerson?.name ?? ''
    });
  }

  answer(conversationId: string, callId: string): void {
    this.sendCall(conversationId, { v: 1, t: 'call', a: 'join', id: callId });
  }

  decline(conversationId: string, callId: string): void {
    this.sendCall(conversationId, { v: 1, t: 'call', a: 'decline', id: callId });
  }

  hangUp(conversationId: string, callId: string): void {
    this.sendCall(conversationId, { v: 1, t: 'call', a: 'leave', id: callId });
  }

  private sendCall(conversationId: string, frame: CallFrame): void {
    this.sendEphemeral(conversationId, frame);
  }

  /**
   * The durable half of a call: one row, once per call id.
   *
   * Additive to `RelayStore`, because who writes it is not a decision a screen
   * makes. Since 2026-09-17 it is whoever `DELETE …/session` answered `last` to
   * — the caller when the call never connected, the last to leave when it did —
   * and `state/calls.tsx` is the one caller. A second row for one call is the
   * failure this is trying to avoid, so the spent-id set lives there, beside
   * the thing that knows a call ended.
   */
  postCallEnd(
    conversationId: string,
    callId: string,
    outcome: CallOutcome,
    opts?: { camera?: boolean; durationSeconds?: number }
  ): void {
    const id = freshId('k_');
    const seconds = outcome === 'answered' ? Math.max(0, Math.round(opts?.durationSeconds ?? 0)) : 0;
    const payload = encodeCallEnd(callId, outcome, {
      name: this.selfPerson?.name ?? '',
      camera: opts?.camera === true,
      durationSeconds: seconds
    });
    this.append(conversationId, {
      id,
      conversationId,
      seq: (this.heads.get(conversationId) ?? 0) + this.outbox.size + 1,
      senderId: this.selfPerson?.id ?? '',
      at: this.now(),
      body: '',
      attachments: [],
      kind: 'callend',
      call: { outcome, durationSeconds: seconds, camera: opts?.camera === true },
      pending: 'sending'
    });
    this.outbox.set(id, { conversationId, payload });
    this.post(id, conversationId, payload);
    this.changed();
  }

  // ── cursors on disk ───────────────────────────────────────────────────────

  /**
   * The cursors, under the account that earned them.
   *
   * Keyed by user id and discarded when it differs, because a cursor does not
   * go back: adopting another account's on a shared browser would advance past
   * every message this one has not read, permanently and silently.
   */
  private restoreCursors(): void {
    const mine = this.selfPerson?.id;
    if (!mine) return;
    try {
      const raw = localStorage.getItem(CURSOR_KEY);
      if (!raw) return;
      const held = JSON.parse(raw) as { userId?: unknown; cursors?: unknown };
      if (held.userId !== mine || typeof held.cursors !== 'object' || held.cursors === null) return;
      for (const [id, seq] of Object.entries(held.cursors as Record<string, unknown>)) {
        if (typeof seq === 'number' && seq > 0) this.cursors.set(id, seq);
      }
    } catch {
      // Blocked, cleared, or written by an older build. Starting from nothing
      // costs one pull per conversation and nothing else.
    }
  }

  private persistCursors(): void {
    this.cursorsDirty = false;
    const mine = this.selfPerson?.id;
    if (!mine) return;
    try {
      localStorage.setItem(
        CURSOR_KEY,
        JSON.stringify({ userId: mine, cursors: Object.fromEntries(this.cursors) })
      );
    } catch {
      // A private window with site data blocked. The page still works; it
      // re-pulls what it already has on the next load.
    }
  }
}

// ── wire to view ────────────────────────────────────────────────────────────

function personOf(user: http.WireUser, presence: Presence): Person {
  return {
    id: user.id,
    name: user.name,
    ...(user.handle ? { handle: user.handle } : {}),
    ...(user.avatarUrl ? { avatarUrl: user.avatarUrl } : {}),
    presence
  };
}

/**
 * One account's toggle of one emoji, folded onto a message.
 *
 * A new object every time, because a row that mutated its own message in place
 * would not re-render: `useSyncExternalStore` compares with `Object.is` and the
 * message it already held is the message it would get back. An emoji nobody is
 * on any more is removed rather than left at zero — a chip with no count is a
 * chip somebody has to reason about.
 */
function foldReaction(message: Message, emoji: string, userId: string, on: boolean): Message {
  const held = message.reactions ?? [];
  const out: Reaction[] = [];
  let found = false;
  for (const reaction of held) {
    if (reaction.emoji !== emoji) {
      out.push(reaction);
      continue;
    }
    found = true;
    const userIds = on
      ? reaction.userIds.includes(userId)
        ? reaction.userIds
        : [...reaction.userIds, userId]
      : reaction.userIds.filter((id) => id !== userId);
    if (userIds.length > 0) out.push({ emoji, userIds });
  }
  if (!found && on) out.push({ emoji, userIds: [userId] });
  return { ...message, reactions: out };
}

function kindFromWire(kind: string): Row['kind'] {
  return kind === 'direct' || kind === 'channel' ? kind : 'group';
}

function statusOf(status: string): MeetingStatus {
  switch (status) {
    case 'live':
    case 'ended':
    case 'cancelled':
      return status;
    default:
      return 'scheduled';
  }
}

/**
 * The invitee list, from the two fields it is on the wire.
 *
 * `invitees` is bare ids and stayed that way when roles arrived — it is what
 * every shipped client reads — and `cohosts` is the second tier beside it.
 * `{userId, role}` objects are read too, because that is the shape the contract
 * document names for `PATCH`, and reading both costs four lines.
 */
function inviteesOf(value: unknown, cohosts?: unknown): MeetingInvitee[] {
  if (!Array.isArray(value)) return [];
  const raised = new Set(
    Array.isArray(cohosts) ? cohosts.filter((id): id is string => typeof id === 'string') : []
  );
  const out: MeetingInvitee[] = [];
  for (const entry of value) {
    if (typeof entry === 'string') {
      out.push({ userId: entry, role: raised.has(entry) ? 'cohost' : 'attendee' });
      continue;
    }
    if (typeof entry !== 'object' || entry === null) continue;
    const row = entry as { userId?: unknown; role?: unknown };
    if (typeof row.userId !== 'string') continue;
    const role = row.role === 'host' || row.role === 'cohost' ? row.role : 'attendee';
    out.push({ userId: row.userId, role });
  }
  return out;
}

function policyOf(value: unknown): MeetingPolicy {
  if (typeof value !== 'object' || value === null) return DEFAULT_POLICY;
  const row = value as Record<string, unknown>;
  const who = (key: string): MeetingPolicy['mic'] => {
    const held = row[key];
    if (held === 'host' || held === 'everyone') return held;
    if (Array.isArray(held) && held.every((v) => typeof v === 'string')) return held as string[];
    return 'everyone';
  };
  const lobby = row['lobby'];
  return {
    mic: who('mic'),
    camera: who('camera'),
    screen: who('screen'),
    lobby: lobby === 'everyone' || lobby === 'off' ? lobby : 'strangers'
  };
}

// ── the one store, and the hook ─────────────────────────────────────────────

/**
 * The one store, and the one place it is constructed.
 *
 * A module-level instance rather than a context: there is exactly one store per
 * page, `/m/:token` included, and threading a provider through the tree buys
 * nothing that a change to this line does not.
 *
 * `new SampleRelayStore()` puts every screen back on the sample set with no
 * server at all — the shell reads `.sample` and says so on a strip. Nothing
 * here reaches the network until the first `subscribe`, so importing this
 * module costs nothing.
 *
 * The type is both, and each half earns its place. The class, because `adopt`,
 * `connection`, `onCallFrames`, `postCallEnd` and `joinByToken` are additive
 * and the composition root calls all five. The interface, because its four
 * optional methods — `setPinned`, `setArchived`, `setMuted`, `react` — are
 * called as `store.setPinned?.(…)` by a row that draws the affordance from the
 * conversation's own flag. Three of the four have no route behind them on
 * relay-server, so they are absent here and the `?.` is what makes that
 * absence mean "nothing happens" rather than "it crashed".
 */
export const store: LiveRelayStore & RelayStore = sampleMode()
  ? // `?sample=1` in development only — `sample-mode.ts` says why, and why it
    // cannot survive a build. The cast is the price of one module-level
    // singleton: the sample store answers every method the root calls (see the
    // end of `sample-store.ts`) but is not the live class.
    (new SampleRelayStore() as unknown as LiveRelayStore & RelayStore)
  : new LiveRelayStore();

/**
 * Read a slice of the store, and re-render when it changes.
 *
 * `useSyncExternalStore` compares the selected value with `Object.is`, so a
 * selector that builds a fresh array or object on every call re-renders on
 * every notification. Select the array the store already holds — every getter
 * above returns the same one until something it was derived from changes.
 */
export function useStore<T>(selector: (s: RelayStore) => T): T {
  const read = useCallback(() => selector(store), [selector]);
  return useSyncExternalStore((listener) => store.subscribe(listener), read, read);
}
